import React, { useCallback, useEffect, useState } from 'react';
import { AlertCircle, AlertTriangle, CheckCircle, Loader2, RotateCw, ShieldAlert } from 'lucide-react';
import { getErrorMessage } from '../../services/apiService';
import {
  clearNcRotationHold,
  getNcRotationStatus,
  NcRotationStatus,
  resolveNcRotation,
  rotateNcNow,
  setNcRotationEnabled,
} from '../../services/portalCredentialsService';

const formatUtcDate = (iso: string) => new Date(iso).toLocaleDateString(undefined, { timeZone: 'UTC' });

/**
 * North Carolina STARS automatic password rotation, shown under the NC row on State Credentials.
 *
 * Three strikes revoke the RACF ID, so every action that signs on or changes the secret is a two-step
 * click, and an interrupted rotation is settled by a person: keep the current password or use the
 * pending one, then one Test Login. See DevDocs/PRD-NC-STARS-Password-Rotation.md.
 */
const NcRotationPanel: React.FC<{ onChanged?: () => void }> = ({ onChanged }) => {
  const [status, setStatus] = useState<NcRotationStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [armed, setArmed] = useState<string | null>(null);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setStatus(await getNcRotationStatus());
    } catch (err) {
      setMessage({ ok: false, text: getErrorMessage(err) });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  /** First click arms the action, the second runs it. */
  const run = async (key: string, action: () => Promise<string>) => {
    if (armed !== key) { setArmed(key); setMessage(null); return; }
    setArmed(null);
    setBusy(key);
    setMessage(null);
    try {
      setMessage({ ok: true, text: await action() });
      await load();
      onChanged?.();
    } catch (err) {
      setMessage({ ok: false, text: getErrorMessage(err) });
    } finally {
      setBusy(null);
    }
  };

  if (loading && !status) {
    return <div className="px-6 pb-4 text-sm text-[#64748b]"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading rotation status…</div>;
  }
  if (!status) return null;

  const button = (key: string, label: string, armedLabel: string, action: () => Promise<string>, danger = false) => (
    <button
      type="button"
      onClick={() => run(key, action)}
      disabled={busy !== null}
      className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors disabled:opacity-50 ${
        armed === key
          ? 'bg-[#AD0000] border-[#AD0000] text-white'
          : danger
          ? 'border-red-300 text-red-700 hover:bg-red-50'
          : 'border-black/[0.08] text-[#0f172a] hover:bg-slate-50'
      }`}
    >
      {busy === key ? <Loader2 className="w-4 h-4 animate-spin inline" /> : armed === key ? armedLabel : label}
    </button>
  );

  return (
    <div className="px-6 pb-5 bg-slate-50/60 border-t border-black/[0.04]">
      <p className="text-xs font-semibold text-[#64748b] uppercase tracking-wide pt-4 mb-3 flex items-center gap-2">
        <RotateCw className="w-3.5 h-3.5" /> Automatic Password Rotation
      </p>

      {status.pending && (
        <div className="mb-3 p-3 rounded-xl border text-sm text-red-800" style={{ backgroundColor: 'rgba(220,38,38,0.08)', borderColor: 'rgba(220,38,38,0.25)' }}>
          <div className="flex items-start gap-2">
            <ShieldAlert className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <div>
              <span className="font-medium">Rotation interrupted.</span> A pending password has been waiting since{' '}
              {status.pendingSince ? new Date(status.pendingSince).toLocaleString() : 'an unknown time'}. STARS may hold either
              password, so nothing signs on to NC (lookups, health check, Test Login) until you choose. Pick one, then run
              Test Login once. Settling also updates {status.mirrorSecrets}.
              <div className="flex flex-wrap gap-2 mt-2">
                {button('keep', 'Keep current password', 'Confirm: keep current', async () =>
                  (await resolveNcRotation(false)).next)}
                {button('use', 'Use pending password', 'Confirm: use pending', async () =>
                  (await resolveNcRotation(true)).next)}
              </div>
            </div>
          </div>
        </div>
      )}

      {status.hold && (
        <div className="mb-3 p-3 rounded-xl border flex items-start gap-2 text-sm text-amber-800" style={{ backgroundColor: 'rgba(234,179,8,0.1)', borderColor: 'rgba(234,179,8,0.3)' }}>
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <span className="font-medium">On hold:</span> {status.hold}
            <div className="mt-2">
              {button('clear', 'Clear hold', 'Confirm: clear hold', async () => { await clearNcRotationHold(); return 'Hold cleared.'; })}
            </div>
          </div>
        </div>
      )}

      {message && (
        <div className={`mb-3 p-3 rounded-xl border flex items-start gap-2 text-sm ${message.ok ? 'text-green-800' : 'text-red-800'}`}
          style={message.ok
            ? { backgroundColor: 'rgba(22,163,74,0.08)', borderColor: 'rgba(22,163,74,0.25)' }
            : { backgroundColor: 'rgba(220,38,38,0.08)', borderColor: 'rgba(220,38,38,0.25)' }}>
          {message.ok ? <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" /> : <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />}
          <span className="break-words">{message.text}</span>
        </div>
      )}

      <div className="text-sm text-[#475569] space-y-1 mb-3">
        <div>
          Status:{' '}
          <span className="font-medium text-[#0f172a]">
            {!status.runsHere ? `Runs in production only (this is ${status.environment})` : status.enabled ? 'On' : 'Off'}
          </span>
        </div>
        {status.nextRotationAt && (
          <div>Next rotation: <span className="font-medium text-[#0f172a]">{formatUtcDate(status.nextRotationAt)}</span> (day 40 of 45)</div>
        )}
        <div>Last run: <span className="font-medium text-[#0f172a]">{status.lastRunDate ?? 'never'}</span></div>
        <div>Also updates: <span className="font-medium text-[#0f172a]">{status.mirrorSecrets}</span></div>
        {!status.credentialReadable && <div className="text-red-700 font-medium">The NC credential could not be read.</div>}
      </div>

      {status.runsHere && (
        <div className="flex flex-wrap gap-2">
          {button('toggle', status.enabled ? 'Turn off' : 'Turn on', status.enabled ? 'Confirm: turn off' : 'Confirm: turn on', async () => {
            const r = await setNcRotationEnabled(!status.enabled);
            return r.enabled ? 'Automatic rotation is on.' : 'Automatic rotation is off.';
          })}
          {button('rotate', 'Rotate now', 'Confirm: change the STARS password now', async () => (await rotateNcNow()).outcome, true)}
        </div>
      )}
      {armed && (
        <p className="text-xs text-[#94a3b8] mt-2">Click the red button again to confirm.</p>
      )}
    </div>
  );
};

export default NcRotationPanel;
